import { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, FileText, AlertTriangle, Clock } from 'lucide-react';
import type { DeadlineTemplate, DeadlineCategory } from '@/types/deadlines';
import { CATEGORY_LABELS, DAY_TYPE_LABELS } from '@/types/deadlines';

interface TemplateSelectorProps {
  templates: DeadlineTemplate[];
  onSelect: (template: DeadlineTemplate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function TemplateSelector({
  templates,
  onSelect,
  onCancel,
  isLoading = false,
}: TemplateSelectorProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DeadlineCategory | 'all'>('all');

  const filteredTemplates = templates.filter(template => {
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        template.title.toLowerCase().includes(searchLower) ||
        template.description?.toLowerCase().includes(searchLower) ||
        template.legal_reference?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    if (categoryFilter !== 'all' && template.category !== categoryFilter) {
      return false;
    }

    return true;
  });

  // Agrupar por categoría
  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<DeadlineCategory, DeadlineTemplate[]>);

  const scopeLabels = {
    nacional: 'Nacional',
    andalucia: 'Andalucía',
    extremadura: 'Extremadura',
    local: 'Local',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar plantillas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as DeadlineCategory | 'all')}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="h-[400px]">
        {filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              No se encontraron plantillas
            </p>
          </div>
        ) : (
          <div className="space-y-6 pr-4">
            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
              <div key={category}>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                  {CATEGORY_LABELS[category as DeadlineCategory]}
                </h3>
                
                <div className="space-y-2">
                  {categoryTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => onSelect(template)}
                      disabled={isLoading}
                      className="w-full p-4 rounded-lg border bg-card text-left transition-colors hover:bg-muted/50 hover:border-primary/50 disabled:opacity-50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {template.is_critical && (
                              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                            )}
                            <h4 className="font-medium truncate">
                              {template.title}
                            </h4>
                          </div>
                          
                          {template.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {template.description}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {template.day_type === 'fecha_fija' 
                                ? `Fecha fija: ${template.fixed_date?.split('-').reverse().join('/')}`
                                : `${template.default_days} ${DAY_TYPE_LABELS[template.day_type].toLowerCase()}`
                              }
                            </div>
                            
                            {template.legal_reference && (
                              <span className="text-xs text-muted-foreground">
                                {template.legal_reference}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {scopeLabels[template.scope]}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="flex justify-end pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
